import { Link } from 'react-router-dom';
import {
  Target, Users, Clock, ArrowRight, Shield,
  CheckCircle2, Briefcase, Building2, Rocket,
  ChevronRight,
} from 'lucide-react';
import { useEffect } from 'react';

// ─── Inject page-scoped styles ────────────────────────────────────────────────
const PAGE_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,500;0,600;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

  /* tokens */
  .flp {
    --c:       #FAF8F4;
    --c2:      #F2EDE3;
    --c3:      #E6DFD1;
    --forest:  #1C3830;
    --fmid:    #2B5244;
    --gold:    #C09A40;
    --gold-bg: #F5EECB;
    --stone:   #796F61;
    --ink:     #18150F;
    --white:   #FFFFFF;
    --red:     #B83232;
    font-family: 'DM Sans', sans-serif;
    color: var(--ink);
    background: var(--c);
  }

  /* ── Keyframes ── */
  @keyframes flp-up {
    from { opacity:0; transform:translateY(18px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes flp-fade {
    from { opacity:0; }
    to   { opacity:1; }
  }
  @keyframes flp-line {
    from { width: 0; }
    to   { width: 100%; }
  }
  @keyframes flp-float {
    0%,100% { transform: translateY(0px); }
    50%     { transform: translateY(-8px); }
  }

  .flp-a1 { animation: flp-up   .55s ease both; }
  .flp-a2 { animation: flp-up   .55s .12s ease both; }
  .flp-a3 { animation: flp-up   .55s .22s ease both; }
  .flp-a4 { animation: flp-up   .55s .32s ease both; }
  .flp-af { animation: flp-fade .7s ease both; }

  /* ── Nav ── */
  .flp-nav {
    display: flex; justify-content: space-between; align-items: center;
    padding: 28px 0 48px;
  }
  .flp-wordmark {
    font-family: 'Lora', serif; font-weight: 600; font-size: 22px;
    color: var(--forest); letter-spacing: -.025em;
  }
  .flp-tag {
    display: inline-flex; align-items: center;
    background: var(--gold-bg); color: var(--gold);
    font-size: 11px; font-weight: 650; letter-spacing: .07em;
    text-transform: uppercase; border-radius: 99px;
    padding: 3px 10px; margin-left: 10px;
    border: 1px solid rgba(192,154,64,.3);
  }
  .flp-nav-btn {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 9px 18px; border-radius: 9px;
    border: 1px solid var(--c3); background: var(--white);
    font-size: 13.5px; font-weight: 500; color: var(--forest);
    cursor: pointer; text-decoration: none;
    transition: all .15s; font-family: 'DM Sans', sans-serif;
    white-space: nowrap;
  }
  .flp-nav-btn:hover { background: var(--c2); border-color: var(--c3); }

  /* ── Hero ── */
  .flp-hero {
    min-height: 88vh; display: flex; align-items: center;
    position: relative; overflow: hidden;
  }
  .flp-hero-bg {
    position: absolute; inset: 0; pointer-events: none;
    background: radial-gradient(ellipse 72% 56% at 70% 44%, rgba(192,154,64,.07) 0%, transparent 70%),
                radial-gradient(ellipse 50% 40% at 10% 80%, rgba(28,56,48,.05) 0%, transparent 60%);
  }
  /* Decorative grid lines */
  .flp-hero-grid {
    position: absolute; inset: 0; pointer-events: none; overflow: hidden; opacity: .35;
  }
  .flp-hero-grid::before {
    content: '';
    position: absolute; top: 0; left: 50%; width: 1px; height: 100%;
    background: linear-gradient(to bottom, transparent, var(--c3) 30%, var(--c3) 70%, transparent);
  }
  .flp-hero-grid::after {
    content: '';
    position: absolute; top: 50%; left: 0; width: 100%; height: 1px;
    background: linear-gradient(to right, transparent, var(--c3) 30%, var(--c3) 70%, transparent);
  }

  .flp-hero-inner {
    display: grid; gap: 60px;
    grid-template-columns: 1fr;
    width: 100%; position: relative; z-index: 1;
    padding: 60px 0 80px;
  }
  @media (min-width: 1024px) {
    .flp-hero-inner { grid-template-columns: 1fr 1fr; align-items: center; }
  }

  /* Eyebrow */
  .flp-eyebrow {
    display: inline-flex; align-items: center; gap: 8px;
    background: var(--gold-bg); color: var(--gold);
    border: 1px solid rgba(192,154,64,.3);
    font-size: 12px; font-weight: 600; letter-spacing: .05em;
    text-transform: uppercase; border-radius: 99px;
    padding: 5px 14px;
  }
  .flp-eyebrow-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: var(--gold); flex-shrink: 0;
    box-shadow: 0 0 0 3px rgba(192,154,64,.25);
  }

  /* H1 */
  .flp-h1 {
    font-family: 'Lora', serif; font-weight: 600;
    font-size: clamp(40px, 6vw, 72px);
    color: var(--forest); letter-spacing: -.03em;
    line-height: 1.08; margin: 18px 0 22px;
  }
  .flp-h1 em {
    font-style: italic; color: var(--gold); position: relative;
    display: inline-block;
  }
  .flp-h1 em::after {
    content: '';
    position: absolute; bottom: -4px; left: 0;
    height: 3px; background: var(--gold); opacity: .35; border-radius: 99px;
    animation: flp-line .8s .6s ease both;
    width: 0; /* animated by keyframe */
  }

  .flp-lead {
    font-size: 18px; color: var(--stone); max-width: 460px;
    line-height: 1.7; font-weight: 400;
  }

  /* CTAs */
  .flp-cta-row { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 32px; }
  .flp-btn-primary {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 14px 28px; border-radius: 10px;
    background: var(--forest); color: var(--c);
    font-size: 14.5px; font-weight: 550; cursor: pointer;
    text-decoration: none; transition: all .16s;
    font-family: 'DM Sans', sans-serif; border: none;
    box-shadow: 0 4px 16px rgba(28,56,48,.2);
  }
  .flp-btn-primary:hover {
    background: var(--fmid);
    box-shadow: 0 6px 22px rgba(28,56,48,.28);
    transform: translateY(-1px);
  }
  .flp-btn-secondary {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 14px 24px; border-radius: 10px;
    border: 1.5px solid var(--c3); background: var(--white);
    font-size: 14.5px; font-weight: 500; color: var(--forest);
    cursor: pointer; text-decoration: none;
    transition: all .15s; font-family: 'DM Sans', sans-serif;
  }
  .flp-btn-secondary:hover { background: var(--c2); border-color: var(--c3); }

  .flp-trust {
    display: flex; align-items: center; gap: 7px;
    font-size: 12.5px; color: var(--stone);
    margin-top: 20px;
  }

  /* ── Stat cards ── */
  .flp-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .flp-stat {
    background: var(--white); border: 1px solid var(--c3);
    border-radius: 16px; padding: 28px 24px;
    transition: all .2s;
    box-shadow: 0 2px 8px rgba(28,56,48,.04);
  }
  .flp-stat:hover {
    border-color: rgba(192,154,64,.4);
    box-shadow: 0 6px 24px rgba(192,154,64,.10);
    transform: translateY(-2px);
  }
  .flp-stat-icon {
    width: 42px; height: 42px; border-radius: 11px;
    background: var(--gold-bg);
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 16px;
  }
  .flp-stat-num {
    font-family: 'Lora', serif; font-size: 34px; font-weight: 600;
    color: var(--forest); letter-spacing: -.04em; line-height: 1;
  }
  .flp-stat-label {
    font-size: 13px; font-weight: 550; color: var(--ink);
    margin-top: 4px;
  }
  .flp-stat-sub {
    font-size: 11.5px; color: var(--stone); margin-top: 4px;
  }

  /* ── Section wrapper ── */
  .flp-section {
    padding: 100px 0;
  }
  .flp-section-sm { padding: 80px 0; }

  .flp-section-head { text-align: center; margin-bottom: 64px; }
  .flp-section-label {
    display: inline-block;
    font-size: 10.5px; font-weight: 700; letter-spacing: .1em;
    text-transform: uppercase; color: var(--gold);
    background: var(--gold-bg); border-radius: 99px;
    padding: 4px 13px; margin-bottom: 16px;
    border: 1px solid rgba(192,154,64,.25);
  }
  .flp-h2 {
    font-family: 'Lora', serif; font-weight: 600;
    font-size: clamp(28px, 4vw, 46px);
    color: var(--forest); letter-spacing: -.025em;
    line-height: 1.15; margin: 0 0 16px;
  }
  .flp-h2-sub {
    font-size: 17px; color: var(--stone);
    max-width: 600px; margin: 0 auto; line-height: 1.65;
  }

  /* ── Phase cards ── */
  .flp-phase-grid {
    display: grid; gap: 24px;
    grid-template-columns: 1fr;
  }
  @media (min-width: 768px) { .flp-phase-grid { grid-template-columns: repeat(3, 1fr); } }

  .flp-phase-card {
    background: var(--white); border: 1px solid var(--c3);
    border-radius: 18px; padding: 36px 32px;
    transition: all .2s; position: relative; overflow: hidden;
  }
  .flp-phase-card::before {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: linear-gradient(90deg, var(--gold-bg), var(--gold), var(--gold-bg));
    opacity: 0; transition: opacity .2s;
  }
  .flp-phase-card:hover {
    border-color: rgba(192,154,64,.3);
    box-shadow: 0 8px 32px rgba(28,56,48,.08);
    transform: translateY(-3px);
  }
  .flp-phase-card:hover::before { opacity: 1; }

  .flp-phase-icon {
    width: 52px; height: 52px; border-radius: 14px;
    background: var(--gold-bg);
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 22px;
  }
  .flp-phase-h { font-family: 'Lora', serif; font-size: 21px; font-weight: 600; color: var(--forest); margin: 0 0 10px; }
  .flp-phase-p { font-size: 14px; color: var(--stone); line-height: 1.65; }
  .flp-phase-foot {
    display: flex; align-items: center; gap: 8px;
    margin-top: 24px; padding-top: 20px;
    border-top: 1px solid var(--c3);
    font-size: 13px;
  }
  .flp-phase-foot-key { font-weight: 600; color: var(--gold); }
  .flp-phase-foot-val { color: var(--stone); }

  /* ── Problem sources band ── */
  .flp-band {
    background: var(--c2);
    border-top: 1px solid var(--c3); border-bottom: 1px solid var(--c3);
  }

  .flp-source-grid {
    display: grid; gap: 20px;
    grid-template-columns: 1fr;
  }
  @media (min-width: 768px) { .flp-source-grid { grid-template-columns: repeat(3, 1fr); } }

  .flp-source-card {
    background: var(--white); border: 1px solid var(--c3);
    border-radius: 18px; padding: 36px 28px;
    text-align: center; transition: all .2s;
  }
  .flp-source-card:hover {
    border-color: rgba(192,154,64,.3);
    box-shadow: 0 6px 24px rgba(28,56,48,.07);
    transform: translateY(-2px);
  }
  .flp-source-orb {
    width: 72px; height: 72px; border-radius: 50%;
    background: var(--gold-bg); border: 2px solid rgba(192,154,64,.2);
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 22px;
    animation: flp-float 4s ease-in-out infinite;
  }
  .flp-source-card:nth-child(2) .flp-source-orb { animation-delay: .8s; }
  .flp-source-card:nth-child(3) .flp-source-orb { animation-delay: 1.6s; }
  .flp-source-h { font-family: 'Lora', serif; font-size: 20px; font-weight: 600; color: var(--forest); margin: 0 0 10px; }
  .flp-source-p { font-size: 13.5px; color: var(--stone); line-height: 1.65; margin-bottom: 20px; }
  .flp-badge {
    display: inline-block; font-size: 11px; font-weight: 650;
    letter-spacing: .04em; text-transform: uppercase;
    background: var(--c2); color: var(--stone);
    border-radius: 99px; padding: 4px 12px;
    border: 1px solid var(--c3);
  }

  /* ── Squad roles ── */
  .flp-roles-grid {
    display: grid; gap: 16px;
    grid-template-columns: 1fr 1fr;
  }
  @media (min-width: 1024px) { .flp-roles-grid { grid-template-columns: repeat(4, 1fr); } }

  .flp-role-card {
    background: var(--white); border: 1px solid var(--c3);
    border-radius: 16px; padding: 28px 24px;
    transition: all .2s;
  }
  .flp-role-card:hover {
    border-color: rgba(192,154,64,.35);
    box-shadow: 0 6px 20px rgba(192,154,64,.08);
    transform: translateY(-2px);
  }
  .flp-role-icon-wrap {
    display: flex; align-items: center; gap: 10px; margin-bottom: 14px;
  }
  .flp-role-icon {
    width: 36px; height: 36px; border-radius: 9px;
    background: var(--gold-bg);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .flp-role-h { font-family: 'Lora', serif; font-size: 16px; font-weight: 600; color: var(--forest); }
  .flp-role-sub { font-size: 12px; font-weight: 600; color: var(--gold); margin-bottom: 14px; letter-spacing: .01em; }
  .flp-role-tasks { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 7px; }
  .flp-role-task {
    display: flex; align-items: flex-start; gap: 7px;
    font-size: 13px; color: var(--stone); line-height: 1.4;
  }
  .flp-check { flex-shrink: 0; margin-top: 1px; }

  /* ── CTA Banner ── */
  .flp-cta-banner {
    background: var(--forest);
    border-radius: 24px; padding: 72px 48px;
    text-align: center; position: relative; overflow: hidden;
  }
  .flp-cta-banner::before {
    content: '';
    position: absolute; top: -60px; right: -60px;
    width: 260px; height: 260px; border-radius: 50%;
    background: rgba(192,154,64,.10);
  }
  .flp-cta-banner::after {
    content: '';
    position: absolute; bottom: -80px; left: -40px;
    width: 220px; height: 220px; border-radius: 50%;
    background: rgba(192,154,64,.07);
  }
  .flp-cta-h {
    font-family: 'Lora', serif; font-size: clamp(28px, 4vw, 44px);
    font-weight: 600; color: var(--c); letter-spacing: -.025em;
    line-height: 1.15; margin: 0 0 14px; position: relative; z-index: 1;
  }
  .flp-cta-h em { font-style: italic; color: var(--gold); }
  .flp-cta-p {
    font-size: 16px; color: rgba(250,248,244,.65);
    max-width: 480px; margin: 0 auto 36px;
    line-height: 1.65; position: relative; z-index: 1;
  }
  .flp-cta-btns { display: flex; flex-wrap: wrap; gap: 12px; justify-content: center; position: relative; z-index: 1; }
  .flp-btn-gold {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 14px 28px; border-radius: 10px;
    background: var(--gold); color: var(--forest);
    font-size: 14.5px; font-weight: 650; cursor: pointer;
    text-decoration: none; transition: all .15s;
    font-family: 'DM Sans', sans-serif; border: none;
    box-shadow: 0 4px 16px rgba(192,154,64,.35);
  }
  .flp-btn-gold:hover {
    background: #D4AC4E;
    transform: translateY(-1px);
    box-shadow: 0 6px 22px rgba(192,154,64,.45);
  }
  .flp-btn-ghost {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 14px 24px; border-radius: 10px;
    border: 1.5px solid rgba(250,248,244,.2); background: transparent;
    font-size: 14.5px; font-weight: 500; color: rgba(250,248,244,.8);
    cursor: pointer; text-decoration: none;
    transition: all .15s; font-family: 'DM Sans', sans-serif;
  }
  .flp-btn-ghost:hover {
    border-color: rgba(250,248,244,.45);
    background: rgba(250,248,244,.07);
    color: var(--c);
  }

  /* ── Footer ── */
  .flp-footer {
    border-top: 1px solid var(--c3);
    padding: 40px 0;
    display: flex; flex-direction: column; align-items: center;
    gap: 20px;
  }
  @media (min-width: 768px) {
    .flp-footer { flex-direction: row; justify-content: space-between; }
  }
  .flp-footer-brand { font-family: 'Lora', serif; font-weight: 600; font-size: 18px; color: var(--forest); }
  .flp-footer-links { display: flex; gap: 28px; }
  .flp-footer-link { font-size: 13px; color: var(--stone); text-decoration: none; transition: color .14s; }
  .flp-footer-link:hover { color: var(--forest); }
  .flp-footer-copy { font-size: 12.5px; color: var(--stone); }

  /* ── Utility ── */
  .flp-container { max-width: 1160px; margin: 0 auto; padding: 0 24px; }
  @media (min-width: 640px) { .flp-container { padding: 0 36px; } }
  @media (min-width: 1024px) { .flp-container { padding: 0 48px; } }

  .flp-divider { height: 1px; background: var(--c3); margin: 0; }
`;

function ForgeStyles() {
  useEffect(() => {
    const id = 'flp-styles';
    if (!document.getElementById(id)) {
      const el = document.createElement('style');
      el.id = id; el.textContent = PAGE_STYLES;
      document.head.appendChild(el);
    }
  }, []);
  return null;
}

// ─── Stat card data ───────────────────────────────────────────────────────────
const STATS = [
  { Icon: Target,    num: '0',  label: 'Problems',     sub: 'Being sourced'    },
  { Icon: Users,     num: '0',  label: 'Active Squads', sub: 'Recruiting now'  },
  { Icon: Building2, num: '0',  label: 'Partners',      sub: 'In discussions'  },
  { Icon: Rocket,    num: 'Q3', label: 'Launch',        sub: 'Coming 2026'     },
];

// ─── Phase data ───────────────────────────────────────────────────────────────
const PHASES = [
  {
    Icon: Clock,
    title: '90-Day Journey',
    desc: 'Structured timeline from squad formation to market validation. Each phase has clear milestones and deliverables.',
    footKey: 'Launch:',
    footVal: 'Q3 2026',
  },
  {
    Icon: Briefcase,
    title: 'Problem Bank',
    desc: 'Curated real-world business challenges sourced from analysts and partner companies. Ready for squads to tackle.',
    footKey: 'Status:',
    footVal: 'Being built',
  },
  {
    Icon: Users,
    title: 'Squad Structure',
    desc: 'Four members with defined roles and individual KPIs — built for accountability, collaboration, and results.',
    footKey: 'Recruiting:',
    footVal: 'Founding squads',
  },
];

// ─── Problem sources ──────────────────────────────────────────────────────────
const SOURCES = [
  {
    Icon: Target,
    title: 'Analyst-Sourced',
    desc: 'Our business analysts are conducting interviews and deep market research to surface actionable problems.',
    badge: 'In Progress',
  },
  {
    Icon: Building2,
    title: 'Company-Submitted',
    desc: 'Reaching out to partners for real operational challenges their teams face today.',
    badge: 'Coming Soon',
  },
  {
    Icon: Rocket,
    title: 'Opportunities',
    desc: 'Identifying market gaps where student squads can build transformative, venture-ready businesses.',
    badge: 'In Discovery',
  },
];

// ─── Squad roles ──────────────────────────────────────────────────────────────
const ROLES = [
  { Icon: Target,   title: 'Project Lead',   sub: 'Coordination & Delivery', tasks: ['Weekly progress reports', 'Milestone tracking'] },
  { Icon: Users,    title: 'Market Analyst', sub: 'Research & Validation',   tasks: ['Customer interviews',    'Competitor analysis']  },
  { Icon: Briefcase,title: 'Product Lead',   sub: 'Solution Design',         tasks: ['MVP requirements',       'User flows']            },
  { Icon: Rocket,   title: 'Finance Lead',   sub: 'Business Model',          tasks: ['Financial projections',  'Unit economics']        },
];

// ─── Page component ───────────────────────────────────────────────────────────
export default function ForgeLandingPage() {
  return (
    <>
      <ForgeStyles />
      <div className="flp">

        {/* ── Hero ─────────────────────────────────────────────────── */}
        <section className="flp-hero">
          <div className="flp-hero-bg" />
          <div className="flp-hero-grid" />

          <div className="flp-container" style={{ width: '100%' }}>
            {/* Nav */}
            <nav className="flp-nav flp-af">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span className="flp-wordmark">Vett &amp; Venture</span>
                <span className="flp-tag">The Forge</span>
              </div>
              <Link to="/forge/auth" className="flp-nav-btn">
                Squad Portal <ChevronRight size={14} />
              </Link>
            </nav>

            {/* Hero grid */}
            <div className="flp-hero-inner">
              {/* Left copy */}
              <div>
                <div className="flp-eyebrow flp-a1">
                  <span className="flp-eyebrow-dot" />
                  Launching Q3 2026 — Join the Waitlist
                </div>

                <h1 className="flp-h1 flp-a2">
                  Forge Your Future<br />
                  in <em>90 Days</em>
                </h1>

                <p className="flp-lead flp-a3">
                  Student squads tackle real-world business problems. We're building
                  the problem bank now — be among the first to join when we launch.
                </p>

                <div className="flp-cta-row flp-a4">
                  <Link to="/forge/register" className="flp-btn-primary">
                    Join the Waitlist <ArrowRight size={16} />
                  </Link>
                  <Link to="/forge/auth" className="flp-btn-secondary">
                    Squad Login
                  </Link>
                </div>

                <div className="flp-trust flp-a4">
                  <Shield size={13} style={{ color: 'var(--gold)', flexShrink: 0 }} />
                  Early access for founding squads only
                </div>
              </div>

              {/* Stat cards */}
              <div className="flp-stats flp-a3">
                {STATS.map(({ Icon, num, label, sub }, i) => (
                  <div key={i} className="flp-stat">
                    <div className="flp-stat-icon">
                      <Icon size={20} style={{ color: 'var(--gold)', strokeWidth: 1.7 }} />
                    </div>
                    <div className="flp-stat-num">{num}</div>
                    <div className="flp-stat-label">{label}</div>
                    <div className="flp-stat-sub">{sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="flp-divider" />

        {/* ── Program phases ───────────────────────────────────────── */}
        <section className="flp-section">
          <div className="flp-container">
            <div className="flp-section-head">
              <span className="flp-section-label">The Journey</span>
              <h2 className="flp-h2">The Forge Phase</h2>
              <p className="flp-h2-sub">
                A 90-day intensive where student squads transform real market problems
                into venture-ready opportunities.
              </p>
            </div>

            <div className="flp-phase-grid">
              {PHASES.map(({ Icon, title, desc, footKey, footVal }, i) => (
                <div key={i} className="flp-phase-card">
                  <div className="flp-phase-icon">
                    <Icon size={24} style={{ color: 'var(--gold)', strokeWidth: 1.65 }} />
                  </div>
                  <h3 className="flp-phase-h">{title}</h3>
                  <p className="flp-phase-p">{desc}</p>
                  <div className="flp-phase-foot">
                    <span className="flp-phase-foot-key">{footKey}</span>
                    <span className="flp-phase-foot-val">{footVal}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Problem sources band ──────────────────────────────────── */}
        <div className="flp-band">
          <section className="flp-section">
            <div className="flp-container">
              <div className="flp-section-head">
                <span className="flp-section-label">Problem Bank</span>
                <h2 className="flp-h2">Building Our Problem Bank</h2>
                <p className="flp-h2-sub">
                  We're actively sourcing high-quality problems from three channels
                  so every squad gets a meaningful challenge.
                </p>
              </div>

              <div className="flp-source-grid">
                {SOURCES.map(({ Icon, title, desc, badge }, i) => (
                  <div key={i} className="flp-source-card">
                    <div className="flp-source-orb">
                      <Icon size={28} style={{ color: 'var(--gold)', strokeWidth: 1.6 }} />
                    </div>
                    <h3 className="flp-source-h">{title}</h3>
                    <p className="flp-source-p">{desc}</p>
                    <span className="flp-badge">{badge}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>

        {/* ── Squad roles ───────────────────────────────────────────── */}
        <section className="flp-section">
          <div className="flp-container">
            <div className="flp-section-head">
              <span className="flp-section-label">Team Structure</span>
              <h2 className="flp-h2">Squad Roles</h2>
              <p className="flp-h2-sub">
                Each squad has four members with distinct responsibilities,
                individual KPIs, and clear ownership.
              </p>
            </div>

            <div className="flp-roles-grid">
              {ROLES.map(({ Icon, title, sub, tasks }, i) => (
                <div key={i} className="flp-role-card">
                  <div className="flp-role-icon-wrap">
                    <div className="flp-role-icon">
                      <Icon size={17} style={{ color: 'var(--gold)', strokeWidth: 1.7 }} />
                    </div>
                    <span className="flp-role-h">{title}</span>
                  </div>
                  <div className="flp-role-sub">{sub}</div>
                  <ul className="flp-role-tasks">
                    {tasks.map((t, j) => (
                      <li key={j} className="flp-role-task">
                        <CheckCircle2
                          size={14}
                          className="flp-check"
                          style={{ color: 'var(--gold)', strokeWidth: 1.7 }}
                        />
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA Banner ────────────────────────────────────────────── */}
        <section className="flp-section-sm">
          <div className="flp-container">
            <div className="flp-cta-banner">
              <h2 className="flp-cta-h">
                Be the First to <em>Join</em>
              </h2>
              <p className="flp-cta-p">
                We're launching in Q3 2026. Join the waitlist to be notified
                the moment applications open.
              </p>
              <div className="flp-cta-btns">
                <Link to="/forge/register" className="flp-btn-gold">
                  Join the Waitlist <ArrowRight size={16} />
                </Link>
                <Link to="/forge/auth" className="flp-btn-ghost">
                  Squad Login
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ── Footer ────────────────────────────────────────────────── */}
        <footer>
          <div className="flp-container">
            <div className="flp-footer">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className="flp-footer-brand">Vett &amp; Venture</span>
                <span className="flp-tag" style={{ marginLeft: 0 }}>The Forge</span>
              </div>
              <nav className="flp-footer-links">
                <Link to="/forge"        className="flp-footer-link">Home</Link>
                <Link to="/forge/auth"   className="flp-footer-link">Squad Portal</Link>
                <Link to="/forge/submit" className="flp-footer-link">Partner Interest</Link>
              </nav>
              <span className="flp-footer-copy">© 2026 Vett &amp; Venture</span>
            </div>
          </div>
        </footer>

      </div>
    </>
  );
}
